import React, { useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { Store } from 'pullstate';
import StarIcon from '@mui/icons-material/Star';
import { getZeroMatrix, PathwordStoreType } from '../PathwordStore';
import { isAdjacent, violatesConsonantVowelPattern, positionMatches, getLineOrientationsForPath, determineHighlights } from '../pathwordUtils';
import { Position } from '../../../../utils/types';
import GridCell from './GridCell';
import { isMobile } from 'react-device-detect';
import '../../shared/puzzles.less';
import '../Pathword.less';

// So for the notation matrix[row][col], row would pick a subarray
// i.e. a word row, and col would pick a letter from that row.
// It equates to [y,x] which is typical in this context.

const startingCorner: Position = [0,0];

const SUCCESS_ANIMATION_DELAY = 150;
let continueTimeout: NodeJS.Timeout | null = null;

let lastTouchedCell: Position = [-1, -1];

type PathwordGridProps = {
  store: Store<PathwordStoreType>;
  onSolve: () => void;
};

const PathwordGrid = ({ store, onSolve }: PathwordGridProps) => {
  const { vowelMatrix, cluetext, puzzleSize, path, pathLength, pathHead, successAnimationGrid, revealed, solved, solutionPath, hintLetters, enabledHints, hintPositions }
    = store.useState();

  const reversedClueStringForQueue = [...cluetext].reverse();

  const [isPathing, setIsPathing] = useState(false);
  // Initialize letterQueue based on current pathLength to handle component remounting
  const [letterQueue, setLetterQueue] = useState(
    pathLength > 0
      ? reversedClueStringForQueue.slice(0, -pathLength)
      : reversedClueStringForQueue
  );

  const nextLetter = letterQueue.at(-1);

  // Check if a cell is an enabled hint cell that requires a specific position in the path
  const violatesHintConstraint = (row: number, col: number) => {
    const posKey = `${row}_${col}`;
    const hintIndex = hintPositions[posKey];

    // Check if the NEXT LETTER we're trying to place is an enabled hint letter
    const nextLetterHintPosition = hintLetters.indexOf(pathLength);
    const nextLetterIsEnabledHint = nextLetterHintPosition !== -1 && enabledHints.includes(nextLetterHintPosition);

    if (nextLetterIsEnabledHint) {
      // Next letter IS an enabled hint - only allow placing it in its designated cell
      return hintIndex !== pathLength;
    }

    // Next letter is NOT a hint - check if we're trying to place it in a hint cell
    if (hintIndex === undefined) return false; // Not a hint cell, allow placement

    const hintLetterPosition = hintLetters.indexOf(hintIndex);
    const isEnabledHint = hintLetterPosition !== -1 && enabledHints.includes(hintLetterPosition);
    if (!isEnabledHint) return false; // Not an enabled hint, allow placement

    // This IS an enabled hint cell - only allow if current path position matches hint index
    return hintIndex !== pathLength;
  };

  const clearPathAfterCell = (row: number, col: number) => {
    const selectedPathValue = path[row][col];
    const modifiedPath = cloneDeep(path);
    for (let i = 0; i < modifiedPath.length; i++) {
      for (let j = 0; j < modifiedPath[i].length; j++) {
        if (modifiedPath[i][j] > selectedPathValue) {
          modifiedPath[i][j] = 0;
        }
      }
    }

    store.update(s => {
      s.path = modifiedPath;
      s.pathLength = selectedPathValue;
      s.pathHead = [row, col];
      s.lines = getLineOrientationsForPath(modifiedPath);
    });

    // so that we can pass it to updateHighlights, since letterQueue is stale here
    const newLetterQueue = reversedClueStringForQueue.slice(0, -selectedPathValue);
    setLetterQueue(newLetterQueue);
    updateHighlights(newLetterQueue.at(-1)!, row, col, modifiedPath);
  };

  // path is passed as a param because this happens before the state update goes out
  // so using the path variable would have stale data, which messes with backtracking
  const updateHighlights = (nextLetter: string, row: number, col: number, updatedPath: number[][]) => {
    // Calculate path length from updatedPath (can't use pathLength from store because it's stale -
    // this function is called AFTER modifying the path but BEFORE the store state updates)
    let updatedPathLength = 0;
    for (let r = 0; r < updatedPath.length; r++) {
      for (let c = 0; c < updatedPath[r].length; c++) {
        if (updatedPath[r][c] > 0) updatedPathLength++;
      }
    }

    store.update(s => {
      if (updatedPathLength === (puzzleSize * puzzleSize)) {
        s.highlights = getZeroMatrix(puzzleSize);
      } else {
        s.highlights = determineHighlights(
          row,
          col,
          nextLetter,
          updatedPath,
          puzzleSize,
          vowelMatrix,
          hintPositions,
          hintLetters,
          enabledHints,
          cluetext,
          updatedPathLength
        );
      }
    });
  };

  const addStepToPath = (row: number, col: number) => {
    const modifiedPath = cloneDeep(path);
    modifiedPath[row][col] = pathLength + 1;
    store.update(s => {
      s.path = modifiedPath;
      s.pathLength = pathLength + 1;
      s.pathHead = [row, col];
      s.lines = getLineOrientationsForPath(modifiedPath);
    });
    const newLetterQueue = letterQueue.slice(0, letterQueue.length - 1);
    setLetterQueue(newLetterQueue);
    updateHighlights(newLetterQueue.at(-1)!, row, col, modifiedPath);
    return modifiedPath; // for the success verification
  };

  const startPath = (row: number, col: number) => {
    if (revealed || solved) return;

    const mouseUpListener = () => {
      setIsPathing(false);
      document.removeEventListener('mouseup', mouseUpListener!);
    };
    document.addEventListener('mouseup', mouseUpListener);

    if (pathLength === 0 && !positionMatches([row, col], startingCorner)) {
      // pulse "start here" message in starting square
      return;
    }

    // If the clicked cell is already in path, erase path AFTER this cell
    // so that this cell is now head.
    if (path[row][col]) {
      clearPathAfterCell(row, col);
      setIsPathing(true);
      return;
    }

    // If the clicked cell isn't a valid move from the current pathHead, do nothing
    if (pathHead && !isAdjacent([row, col], pathHead)) {
      return;
    }

    // If the clicked cell isn't a valid move based on consonant/vowel patter, do nothing
    if (violatesConsonantVowelPattern(nextLetter!, row, col, vowelMatrix)) {
      return;
    }

    // If the clicked cell is an enabled hint cell that requires a different position, reject
    if (violatesHintConstraint(row, col)) {
      return;
    }

    const modifiedPath = addStepToPath(row, col);
    if (pathLength + 1 === (puzzleSize * puzzleSize)) {
      if (isValidSolution(modifiedPath)) {
        onSolve();
        setIsPathing(false);
        triggerSuccessAnimation(row, col);
      } else {
        setTimeout(() => {
          store.update(s => {
            s.almostThereIsOpen = true;
          });
        }, 200);
      }
    } else {
      setIsPathing(true);
    }
  };

  const isValidSolution = (suggestedSolution: number[][]) => {
    return isEqual(suggestedSolution, solutionPath);
  };

  const continuePath = (row: number, col: number) => {
    if (!isPathing) return;
    if (revealed || solved) return;
    // This catches an edge-case that mostly seems to trigger during mobile-emulation
    // during development where the currentCell is not part of the grid at all but
    // rather the app or something outside the grid, and so both row and col are NaN.
    // but we can basically just ignore that and skip it. again, only happens during
    // emulation anyway. although it also seems to happen when the AlmostThereDialog comes up.
    if (isNaN(row) || isNaN(col)) {
      return;
    }

    // This has a short delay so we dont trigger it on the wrong square when pathing diagonally
    if (continueTimeout) {
      clearTimeout(continueTimeout);
      continueTimeout = null;
    }

    continueTimeout = setTimeout(() => {
      if (path[row][col]) {
        const [prevRow, prevCol] = pathHead!;
        const previousPosition = path[prevRow][prevCol] - 1;
        if (path[row][col] === previousPosition) {
          clearPathAfterCell(row, col);
        }
        return;
      }

      if (!isAdjacent([row, col], pathHead!)) {
        return;
      }

      if (violatesConsonantVowelPattern(nextLetter!, row, col, vowelMatrix)) {
        return;
      }

      // If the cell is an enabled hint cell that requires a different position, reject
      if (violatesHintConstraint(row, col)) {
        return;
      }

      const modifiedPath = addStepToPath(row, col);
      if (pathLength + 1 === (puzzleSize * puzzleSize)) {
        if (isValidSolution(modifiedPath)) {
          onSolve();
          setIsPathing(false);
          triggerSuccessAnimation(row, col);
        } else {
          setTimeout(() => {
            store.update(s => {
              s.almostThereIsOpen = true;
            });
          }, 200);
        }
      }
    }, 35);
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    let currentCell: Element = document.elementFromPoint(touch.clientX, touch.clientY)!;
    if (!currentCell) return;
    // for when it triggers on the next-letter hint instead of the cell containing it
    if (!currentCell.classList.contains('matrixCell')) {
      currentCell = currentCell.parentElement!;
    }

    let row = parseInt(currentCell!.getAttribute('data-rowindex')!);
    let col = parseInt(currentCell!.getAttribute('data-colindex')!);

    // This catches an edge-case that mostly seems to trigger during mobile-emulation
    // during development where the currentCell is not part of the grid at all but
    // rather the app or something outside the grid, and so both row and col are NaN.
    // but we can basically just ignore that and skip it. again, only happens during
    // emulation anyway. although it also seems to happen when the AlmostThereDialog comes up.
    if (isNaN(row)) {
      return;
    }

    // Swiped to a new cell.
    if (pathHead && (row !== pathHead[0] || col !== pathHead[1])) {
      // This if statement prevents this from triggering over and over
      // on the same cell, which gets extremely glitchy and stops it from working.
      if (positionMatches([row, col], lastTouchedCell)) return;
      lastTouchedCell = [row, col];
      continuePath(row, col);
    }
  };

  const triggerSuccessAnimation = (row: number, col: number) => {
    // cuz store state would be stale
    const localCopyOfSuccessGrid = cloneDeep(successAnimationGrid);

    const triggerNextWave = (r: number, c: number) => {
      // Change the cell at row, col to 1
      store.update(s => { s.successAnimationGrid[r][c] = 1; });
      localCopyOfSuccessGrid[r][c] = 1;

      // Check adjacent cells
      const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]]; // Up, Down, Left, Right
      for (const [dr, dc] of directions) {
        const newRow = r + dr;
        const newCol = c + dc;
        if (newRow >= 0 && newRow < puzzleSize && newCol >= 0 && newCol < puzzleSize && localCopyOfSuccessGrid[newRow][newCol] === 0) {
          setTimeout(() => triggerNextWave(newRow, newCol), SUCCESS_ANIMATION_DELAY);
        }
      }
    };

    triggerNextWave(row, col);
  };

  const reset = () => {
    store.update(s => {
      s.path = getZeroMatrix(puzzleSize);
      s.lines = {};
      s.pathHead = null;
      s.pathLength = 0;
      s.highlights = getZeroMatrix(puzzleSize);
      s.solved = false;
      s.successAnimationGrid = getZeroMatrix(puzzleSize);
    });
    setLetterQueue(reversedClueStringForQueue);
  };

  const undo = () => {
    alert(`To undo part of your path, simply ${isMobile ? 'tap' : 'click'} anywhere along the path and everything after that point will be cleared.`);
  };

  const usedLetters = cluetext.slice(0, pathLength);
  const unusedLetters = cluetext.slice(pathLength);

  // Helper to check if an index is an enabled hint
  const isEnabledHint = (index: number) => {
    const hintPosition = hintLetters.indexOf(index);
    return hintPosition !== -1 && enabledHints.includes(hintPosition);
  };

  return (
    <>
      <div className="clueString">
        <span className="usedLetters">
          {usedLetters.map((letter, index) => (
            <span
              key={index}
              className={isEnabledHint(index) ? 'hintLetter' : ''}
            >
              {letter}
            </span>
          ))}
        </span>
        <span className="unusedLetters">
          {unusedLetters.map((letter, index) => {
            const absoluteIndex = pathLength + index;
            return (
              <span
                key={absoluteIndex}
                className={isEnabledHint(absoluteIndex) ? 'hintLetter' : ''}
              >
                {letter}
              </span>
            );
          })}
        </span>
      </div>

      <div className={`matrixGrid ${solved ? 'solved' : ''}`} onTouchMove={e => onTouchMove(e)}>
        <div className="goldStar"><StarIcon/></div>
        {vowelMatrix.map((row, rowIndex) => (
          <div key={rowIndex} className="matrixRow">
            {row.map((letter, columnIndex) => (
              <GridCell
                store={store}
                key={columnIndex}
                row={rowIndex}
                col={columnIndex}
                isVowel={vowelMatrix[rowIndex][columnIndex] === 0}
                startPath={startPath}
                continuePath={continuePath}
              />
            ))}
          </div>
        ))}
      </div>

      {!(solved || revealed) && <div className="undoButtons">
        <button className="clearButton" onClick={reset}>Clear</button>
        <button className="undoButton" onClick={undo}>Undo</button>
      </div>}

      {revealed &&
        <div className="outcomeBanner">
          Solution revealed. Try again tomorrow!
        </div>
      }
    </>
  );
};

export default PathwordGrid;